/**
 * The letter. Two voices: the lover, who keeps trying to measure it, and the guide, who shows why each unit breaks.
 * Every chapter is a short rhythm: a personal question, something to watch, one thing to do, a discovery, and the
 * lover's response. Plain words first; the numbers and sources live in each chapter's notes, which open on request.
 *
 * Fill in the title, the dedication and, if you like, one personal line only you could write.
 */
export const TITLE = {
  name: 'Bending the Milky Way',
  tagline: 'A love letter to Shyla and the wonder of the universe'
};
export const DEDICATION = { to: 'Shyla, the love of my life', from: 'Jason' };

export interface Line { voice: 'lover' | 'expert'; text: string; }

export interface ChapterText {
  id: string; title: string; unit: string; place: string; bridge: string;
  intro: Line[]; lever: string; payoff: Line[];
  /** the science, optional: numbers, sources, what is exaggerated */
  notes: string[];
}

export const LETTER = {
  opening: [
    { voice: 'lover', text: 'I love you. Deeper than the Mariana Trench and wider than the Milky Way on its longest axis.' },
    { voice: 'lover', text: 'That turned out to be more complicated than it sounds. Come and see.' }
  ] as Line[],

  chapters: [
    {
      id: 'deeper', title: 'Deeper', unit: 'depth', place: 'The Sun',
      bridge: 'First stop: how deep is deep?',
      intro: [
        { voice: 'lover', text: 'Deep was my first try. The Mariana Trench is eleven kilometres down, and here’s the thing that got me: a clock at the bottom runs a little slower than a clock at the top. Really. People have measured it.' },
        { voice: 'expert', text: 'Anything heavy makes a dip in time. The lower you sit in the dip, the slower your clock runs. The trench’s dip is tiny. So let’s go somewhere with a much bigger one: the Sun.' },
        { voice: 'expert', text: 'Light feels the dip too. Here’s a beam of starlight heading past the Sun toward a telescope on Earth.' }
      ],
      lever: 'Move the light closer to the Sun and watch its path curve more. When you think it will land in the telescope on Earth, press Send the light.',
      payoff: [
        { voice: 'expert', text: 'It bent twice as much as a plain pull would have. That extra half is space itself curving, not just time. A photo of exactly this, taken in 1919, made Einstein famous overnight.' },
        { voice: 'lover', text: 'So “deep” does come with a number. At the Sun, time runs about two millionths slower. I checked. It’s not nearly enough.' }
      ],
      notes: [
        'Gravity around the Sun is turned up about 60,000 times in this picture so the bend is visible. The real deflection of starlight grazing the Sun is 1.75 seconds of arc, first measured by Eddington’s eclipse expedition in 1919.',
        'A clock at the bottom of the Mariana Trench runs about 37 microseconds a year slower than one at sea level. A clock at the Sun’s surface runs about 67 seconds a year slower than one far away. Same rule both times: time runs slower lower in a gravity well.',
        'Why twice the Newtonian value: in Einstein’s theory both time and space are curved near a mass, and for light the two contributions are equal. A particle pulled by a plain force would bend half as much.'
      ]
    },
    {
      id: 'wider', title: 'Wider', unit: 'width', place: 'The Milky Way, from above',
      bridge: 'Second stop: how wide is wide?',
      intro: [
        { voice: 'lover', text: 'Fine. Wide, then. The Milky Way is about a hundred thousand light-years across. I figured that was the biggest ruler anyone could hand me.' },
        { voice: 'expert', text: 'Here it is from above. The Sun is that one speck about two thirds of the way out, going round once every 230 million years.' },
        { voice: 'expert', text: 'There’s a catch. The stars out here move too fast for the matter we can see. Something heavy and invisible must be holding them. We can weigh it: add mass until the Sun moves at the speed the Gaia telescope measured, 230 kilometres a second.' }
      ],
      lever: 'Drag the slider to add invisible mass and watch the Sun speed up. Stop when it reads 230 km/s.',
      payoff: [
        { voice: 'expert', text: 'To move the Sun that fast, the galaxy needs about five times more mass than all its stars and gas put together. That’s dark matter: a shape we can measure and can’t see.' },
        { voice: 'lover', text: 'Five times more going on than meets the eye. Yeah. I know that feeling.' }
      ],
      notes: [
        'The Sun orbits the centre of the Milky Way at about 230 km/s, from Gaia and other surveys. Adding up the visible stars and gas gives far too little mass for that speed. The difference is dark matter, roughly five times the visible mass inside the Sun’s orbit.',
        'The bowl drawn under the galaxy is a picture of slowed time: clocks near the centre run about two millionths slower than clocks far outside. Its depth is exaggerated to be visible.'
      ]
    },
    {
      id: 'time', title: 'Time', unit: 'time', place: 'The stars around the black hole, 26,000 light-years in',
      bridge: 'Third stop: a unit everybody understands.',
      intro: [
        { voice: 'lover', text: 'Okay, time. Everyone understands time. I’ll love you for a thousand years. Simple. Except time is the first thing gravity messes with.' },
        { voice: 'expert', text: 'Watch the two clocks. This is S2, a star that swings around the black hole at the centre of our galaxy. On every close pass its clock falls behind ours. We saw it happen in 2018.' },
        { voice: 'expert', text: 'The close pass also twists its orbit a little, so S2 never comes back to quite the same spot. Watch one lap, then guess where it swings past next time.' }
      ],
      lever: 'When S2 pauses at the far end, click just beside the black hole, inside the gold ring, on the side you think it will swing past. Or press Show me.',
      payoff: [
        { voice: 'expert', text: 'Orbits don’t quite close in curved spacetime. S2 came back on Einstein’s mark, not Newton’s. We watched the real twist in 2020.' },
        { voice: 'lover', text: 'A thousand years, then. Sure. But a thousand years on whose clock?' }
      ],
      notes: [
        'S2 passes Sagittarius A* at about 7,650 km/s, 2.5 percent of the speed of light, 120 times the Earth–Sun distance out. Its clock runs about 0.03 percent slow at that moment, measured in 2018 as a redshift of its light.',
        'The orbit turns by about 12 arcminutes per lap, measured in 2020. Here the turn is drawn 200 times larger so you can see it in a single lap.'
      ]
    },
    {
      id: 'light', title: 'Light', unit: 'intensity', place: 'Looking toward the heart of the galaxy',
      bridge: 'Fourth stop: how bright?',
      intro: [
        { voice: 'lover', text: 'What about intensity? Maybe love gets measured in light. How bright it burns.' },
        { voice: 'expert', text: 'Light weighs nothing, and gravity bends it anyway. Somewhere in this field there’s a dark mass you can’t see. Drag it over a star and watch what the star does.' }
      ],
      lever: 'Drag anywhere to move the invisible mass. Put it right on top of a star and hold it there.',
      payoff: [
        { voice: 'expert', text: 'The star split and flared. From the shape of that flare we can weigh the thing you never saw: about four tenths of a Sun. Surveys toward the centre of the galaxy catch thousands of these a year.' },
        { voice: 'lover', text: 'You’ve bent every bit of light I ever sent your way. I’ve been weighed. I know what I’m worth now.' }
      ],
      notes: [
        'This is gravitational microlensing. A dark mass passing in front of a background star bends its light into two images too close together to separate, and the star appears to brighten for days or weeks. The shape of the brightening gives the mass.',
        'The picture is the exact weak-field bending for a point mass, drawn live for every pixel. When the alignment is perfect the two images merge into an Einstein ring.'
      ]
    },
    {
      id: 'pit', title: 'All the way', unit: 'distance', place: 'Sagittarius A*, the black hole at the centre',
      bridge: 'Last stop: how far would I go?',
      intro: [
        { voice: 'lover', text: 'Here’s the only unit I actually trust. How far I’d go.' },
        { voice: 'expert', text: 'This is Sagittarius A*: four million Suns in a space smaller than Mercury’s orbit. Your clock and Earth’s were set together when you left. Watch them drift apart.' }
      ],
      lever: 'Drag to look around. Press Dive to go in. At the last orbit that holds, you choose.',
      payoff: [],
      notes: [
        'Sagittarius A* has about 4.3 million times the Sun’s mass. Its event horizon is 12.7 million km across. The Event Horizon Telescope imaged its shadow in 2022.',
        'The last orbit that holds is the innermost stable circular orbit, three Schwarzschild radii out. To stay there you move at half the speed of light, and your clock runs at 0.71 of a faraway clock’s. Thirty days parked there are forty-two days on Earth.',
        'The picture is a non-spinning (Schwarzschild) black hole, ray-traced live. The brighter side of the disk is the side moving toward you.'
      ]
    }
  ] as ChapterText[],

  pitHold: [
    { voice: 'lover', text: 'Thirty days by my clock. Forty-two by yours. I’d pay the difference every single time.' }
  ] as Line[],
  pitFall: [
    { voice: 'lover', text: 'All the way in. On your clock I never quite get there. On mine, I’m already here.' }
  ] as Line[],

  epilogue: [
    { voice: 'lover', text: 'So here’s what I learned. Deep is slow time. Wide is a bent ruler. Time depends on whose clock. Light bends toward whatever’s heavy. And falling isn’t a pull at all. It’s what a straight line looks like near something that matters.' },
    { voice: 'lover', text: 'I couldn’t find a unit big enough. “Wide” and “deep” were never going to do it.' },
    { voice: 'lover', text: 'It’s this: you bend my space. Every straight line I follow leads to you.' }
  ] as Line[],

  /** One line only you could write: a memory, a habit, something of hers. Shown just before the last line. Empty shows nothing. */
  personal: '',

  /** The measurements the chapters add to the map, in the order they are taken. */
  measurements: {
    deeper: 'The Sun’s dip in time: two millionths',
    wider: 'The galaxy’s bowl: five parts dark to one lit',
    time: 'S2’s clock: slowed. Its orbit: twisted',
    light: 'One invisible mass, weighed by a flare',
    pit: 'The pit: all of it'
  } as Record<string, string>
};
